/**
 * Service API endpoints
 * 
 * This module provides API functions for service management operations.
 * It handles the mapping between frontend Service types and backend API DTOs,
 * following the same patterns as the customer API implementation.
 * 
 * Backend API endpoints:
 * - GET /api/v1/services - List services with pagination and filtering
 * - POST /api/v1/services - Create new service
 * - GET /api/v1/services/:id - Get service by ID
 * - PUT /api/v1/services/:id - Update service
 * - DELETE /api/v1/services/:id - Delete service
 */

import { api } from '@/api/axios';
import {
  Service,
  ServiceLine,
  ServiceFilters,
  ServiceListResponse,
  ServiceDetailResponse,
  CreateServiceRequest,
  UpdateServiceRequest,
  ServiceTypeCounts,
} from '@/types/service';

// Import Result Types
interface ImportResult {
  totalRows: number;
  successRows: number;
  failedRows: number;
  insertedRows: number;
  updatedRows: number;
  errors?: ImportError[];
  summary: string;
}

interface ApiImportResult {
  total_rows: number;
  success_rows: number;
  failed_rows: number;
  inserted_rows: number;
  updated_rows: number;
  errors?: ImportError[];
  summary: string;
}

interface ImportError {
  row: number;
  column?: string;
  field?: string;
  value?: string;
  message: string;
}

// API Response Types (based on backend DTOs)
interface ApiServiceResponse {
  id: string;
  code: string;
  name: string;
  category_id: string;
  category_name?: string;
  price: number;
  unit?: string;
  default_price?: number; // Service base price (same as price, for clarity)
  custom_price?: number; // Customer-specific or group price from CustomerPrice table
  source_price?: string; // Price source: customer_specific, customer_group, service_base
  status: string;
  room: string;
  service_lines_id: string[];
  service_lines?: ApiServiceLineResponse[];
  createdAt: string;
  updatedAt: string;
}

interface ApiServiceLineResponse {
  id: string;
  code: string;
  service_id: string;
  material_id: string;
  material_name?: string;
  quantity: number;
  lose_rate: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface ApiStandardResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

const buildServiceFilterQuery = (format: 'csv' | 'xlsx', filters?: ServiceFilters) => {
  const params = new URLSearchParams({ format });

  if (filters?.search) {
    params.append('search', filters.search);
  }
  if (filters?.status && filters.status.length > 0) {
    params.append('status', filters.status.join(','));
  }
  if (filters?.categoryId && filters.categoryId.length > 0) {
    params.append('category_id', filters.categoryId.join(','));
  }
  if (filters?.room && filters.room.length > 0) {
    params.append('room', filters.room.join(','));
  }
  if (filters?.priceFrom !== undefined) {
    params.append('price_from', filters.priceFrom.toString());
  }
  if (filters?.priceTo !== undefined) {
    params.append('price_to', filters.priceTo.toString());
  }
  if (filters?.createdFrom) {
    params.append('created_from', filters.createdFrom);
  }
  if (filters?.createdTo) {
    params.append('created_to', filters.createdTo);
  }
  if (filters?.customerCode) {
    params.append('customer_code', filters.customerCode);
  }

  return params.toString();
};

// Mapping functions
const mapApiServiceToFrontend = (apiService: ApiServiceResponse): Service => {
  return {
    id: apiService.id,
    code: apiService.code,
    name: apiService.name,
    categoryId: apiService.category_id,
    categoryName: apiService.category_name,
    price: apiService.price,
    unit: apiService.unit,
    defaultPrice: apiService.default_price,
    customPrice: apiService.custom_price,
    sourcePrice: apiService.source_price,
    status: apiService.status as Service['status'],
    room: apiService.room,
    serviceLinesId: apiService.service_lines_id,
    serviceLines: apiService.service_lines?.map(mapApiServiceLineToFrontend),
    createdAt: apiService.createdAt,
    updatedAt: apiService.updatedAt,
  };
};

const mapApiServiceLineToFrontend = (apiServiceLine: ApiServiceLineResponse) => {
  return {
    id: apiServiceLine.id,
    code: apiServiceLine.code,
    serviceId: apiServiceLine.service_id,
    materialId: apiServiceLine.material_id,
    materialName: apiServiceLine.material_name,
    quantity: apiServiceLine.quantity,
    loseRate: apiServiceLine.lose_rate,
    createdAt: apiServiceLine.createdAt,
    updatedAt: apiServiceLine.updatedAt,
  };
};

const mapFrontendServiceToApi = (service: CreateServiceRequest) => {
  return {
    name: service.name,
    category_id: service.categoryId,
    price: service.price,
    unit: service.unit,
    status: service.status || 'selling',
    room: service.room,
  };
};

const mapApiImportResultToFrontend = (result: ApiImportResult): ImportResult => ({
  totalRows: result.total_rows,
  successRows: result.success_rows,
  failedRows: result.failed_rows,
  insertedRows: result.inserted_rows,
  updatedRows: result.updated_rows,
  errors: result.errors,
  summary: result.summary,
});

export const serviceApi = {
  // Get services list with filters and pagination
  getServices: async (
    page: number = 1,
    pageSize: number = 10,
    filters?: ServiceFilters
  ): Promise<ServiceListResponse> => {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
      
      // Add filters based on backend API
      if (filters?.search) {
        params.append('search', filters.search);
      }

      if (filters?.status && filters.status.length > 0) {
        // Join multiple status values with comma as per backend requirement
        params.append('status', filters.status.join(','));
      }

      if (filters?.categoryId && filters.categoryId.length > 0) {
        // Join multiple category IDs with comma as per backend requirement
        params.append('category_id', filters.categoryId.join(','));
      }

      if (filters?.customerCode) {
        params.append('customer_code', filters.customerCode);
      }

      if (filters?.room && filters.room.length > 0) {
        // Join multiple room values with comma as per backend requirement
        params.append('room', filters.room.join(','));
      }
      
      if (filters?.priceFrom !== undefined) {
        params.append('price_from', filters.priceFrom.toString());
      }
      
      if (filters?.priceTo !== undefined) {
        params.append('price_to', filters.priceTo.toString());
      }
      
      if (filters?.createdFrom) {
        params.append('created_from', filters.createdFrom);
      }
      
      if (filters?.createdTo) {
        params.append('created_to', filters.createdTo);
      }

      const response = await api.get<ApiPaginatedResponse<ApiServiceResponse>>(`/services?${params.toString()}`);

      // Tolerate empty result sets where backend returns data: null
      const rawList = Array.isArray(response.data?.data) ? response.data?.data : [];

      return {
        data: rawList.map(mapApiServiceToFrontend),
        total: response.data.total,
        page: response.data.page,
        pageSize: response.data.pageSize,
      };
    } catch (error: any) {
      console.error('Error fetching services:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch services');
    }
  },

  // Get single service by code
  getService: async (code: string): Promise<Service> => {
    try {
      const response = await api.get<ApiStandardResponse<ApiServiceResponse>>(`/services/${code}`);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiServiceToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error fetching service:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch service');
    }
  },

  // Get service detail with service lines
  getServiceDetail: async (code: string): Promise<ServiceDetailResponse> => {
    try {
      // Get service details
      const service = await serviceApi.getService(code);
      
      // For now, return service with service lines from the main response
      // In the future, you might want to fetch service lines separately
      return {
        service,
        serviceLines: service.serviceLines || [],
        totalServiceLines: service.serviceLines?.length || 0,
      };
    } catch (error: any) {
      console.error('Error fetching service detail:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to fetch service detail');
    }
  },

  // Create new service
  createService: async (data: CreateServiceRequest): Promise<Service> => {
    try {
      const apiData = mapFrontendServiceToApi(data);
      const response = await api.post<ApiStandardResponse<ApiServiceResponse>>('/services', apiData);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiServiceToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error creating service:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to create service');
    }
  },

  // Update service
  updateService: async (data: UpdateServiceRequest & { code: string }): Promise<Service> => {
    try {
      const { code, ...updateData } = data;
      const apiData = mapFrontendServiceToApi(updateData as CreateServiceRequest);
      const response = await api.put<ApiStandardResponse<ApiServiceResponse>>(`/services/${code}`, apiData);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiServiceToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error updating service:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to update service');
    }
  },

  // Delete service
  deleteService: async (code: string): Promise<void> => {
    try {
      await api.delete(`/services/${code}`);
    } catch (error: any) {
      console.error('Error deleting service:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to delete service');
    }
  },

  // Get service type counts for filter tabs with optional filters
  getServiceTypeCounts: async (filters?: Omit<ServiceFilters, 'status'>): Promise<ServiceTypeCounts> => {
    try {
      const params = new URLSearchParams();

      // Add filters (excluding status since we're counting by all statuses)
      if (filters?.search) {
        params.append('search', filters.search);
      }

      if (filters?.categoryId && filters.categoryId.length > 0) {
        params.append('category_id', filters.categoryId.join(','));
      }

      if (filters?.room && filters.room.length > 0) {
        params.append('room', filters.room.join(','));
      }

      if (filters?.priceFrom !== undefined) {
        params.append('price_from', filters.priceFrom.toString());
      }

      if (filters?.priceTo !== undefined) {
        params.append('price_to', filters.priceTo.toString());
      }

      if (filters?.createdFrom) {
        params.append('created_from', filters.createdFrom);
      }

      if (filters?.createdTo) {
        params.append('created_to', filters.createdTo);
      }

      if (filters?.customerCode) {
        params.append('customer_code', filters.customerCode);
      }

      const url = params.toString() ? `/services/count-by-status?${params.toString()}` : '/services/count-by-status';
      const response = await api.get<ApiStandardResponse<{ [key: string]: number }>>(url);

      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }

      const counts = {
        all: response.data.data.all || 0,
        selling: response.data.data.selling || 0,
        pending: response.data.data.pending || 0,
        stop: response.data.data.stop || 0,
      };

      return counts;
    } catch (error: any) {
      console.error('Error fetching service type counts:', error);
      // Return zero counts on error
      return {
        all: 0,
        selling: 0,
        pending: 0,
        stop: 0,
      };
    }
  },

  // Create multiple service lines at once
  createBulkServiceLines: async (serviceId: string, serviceLines: Array<{
    materialId: string;
    quantity: number;
    loseRate: number;
  }>): Promise<ServiceLine[]> => {
    try {
      const payload = serviceLines.map(line => ({
        service_id: serviceId,
        material_id: line.materialId,
        quantity: line.quantity,
        lose_rate: line.loseRate,
      }));

      const response = await api.post('/service-lines/bulk', payload);

      // Map response to frontend format
      return response.data.data.map(mapApiServiceLineToFrontend);
    } catch (error: any) {
      console.error('Error creating bulk service lines:', error);
      throw new Error(
        error.response?.data?.message || 'Failed to create service lines'
      );
    }
  },

  // Create single service line
  createServiceLine: async (serviceId: string, serviceLine: {
    materialId: string;
    quantity: number;
    loseRate: number;
  }): Promise<ServiceLine> => {
    try {
      const payload = {
        service_id: serviceId,
        material_id: serviceLine.materialId,
        quantity: serviceLine.quantity,
        lose_rate: serviceLine.loseRate,
      };

      const response = await api.post<ApiStandardResponse<ApiServiceLineResponse>>('/service-lines', payload);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiServiceLineToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error creating service line:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to create service line');
    }
  },

  // Update service line
  updateServiceLine: async (serviceLineCode: string, serviceLine: {
    serviceId: string;
    materialId: string;
    quantity: number;
    loseRate: number;
  }): Promise<ServiceLine> => {
    try {
      const payload = {
        service_id: serviceLine.serviceId,
        material_id: serviceLine.materialId,
        quantity: serviceLine.quantity,
        lose_rate: serviceLine.loseRate,
      };

      const response = await api.put<ApiStandardResponse<ApiServiceLineResponse>>(`/service-lines/${serviceLineCode}`, payload);
      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }
      return mapApiServiceLineToFrontend(response.data.data);
    } catch (error: any) {
      console.error('Error updating service line:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to update service line');
    }
  },

  // Delete service line
  deleteServiceLine: async (serviceLineCode: string): Promise<void> => {
    try {
      await api.delete(`/service-lines/${serviceLineCode}`);
    } catch (error: any) {
      console.error('Error deleting service line:', error);
      throw new Error(error.apiError?.message || error.message || 'Failed to delete service line');
    }
  },

  // Import services from file
  importServices: async (file: File): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiStandardResponse<ApiImportResult>>('/services/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return mapApiImportResultToFrontend(response.data.data);
  },

  // Export services to file
  exportServices: async (format: 'csv' | 'xlsx', filters?: ServiceFilters): Promise<Blob> => {
    const queryStr = buildServiceFilterQuery(format, filters);
    const response = await api.get(`/services/export?${queryStr}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  importServiceMaterials: async (file: File): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiStandardResponse<ApiImportResult>>('/services/materials/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return mapApiImportResultToFrontend(response.data.data);
  },

  exportServiceMaterials: async (format: 'csv' | 'xlsx', filters?: ServiceFilters): Promise<Blob> => {
    const queryStr = buildServiceFilterQuery(format, filters);
    const response = await api.get(`/services/materials/export?${queryStr}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get import template
  getServiceImportTemplate: async (format: 'csv' | 'xlsx'): Promise<Blob> => {
    const response = await api.get(`/services/import/template?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  getServiceMaterialsImportTemplate: async (format: 'csv' | 'xlsx'): Promise<Blob> => {
    const response = await api.get(`/services/materials/import/template?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};
